import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import BEMHelper from 'react-bem-helper';
import { Trans } from '@lingui/macro';
import { UncontrolledTooltip, Media } from 'reactstrap';
import Button from '@/components/elements/BaseButton';
import Panel from '@/components/elements/Panel';
import { MdInfoOutline } from 'react-icons/md';

import { format, startOfWeek, toDate, addDays, isSameDay, isBefore } from '@/misc/datetime';
import { formatFromTo } from '@/misc/common';
import { canAddToQueue, getFreeSpots, getPublicExceptionReason, hasTimeException } from '@/misc/freeCapacity';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping/index';
import { ConfigKeys } from '@/misc/constants';

import {
    Configuration,
    ServiceType,
    TimeType,
    CompanyStateType,
    BookingStateType,
} from '@/types';
import { ApplicationState } from '@/store';

import './CalendarWeek.css';
import { hasGroupBooking } from '@/misc/serviceHelpers';

const c = new BEMHelper({ name: 'CalendarWeek' });
const tooltipContainer = document.getElementById('bokamera-embedded');

// --- Types & Enums ---

enum SlotStatus {
    BOOKABLE = 'BOOKABLE',
    QUEUEABLE = 'QUEUEABLE',
    FULLY_BOOKED = 'FULLY_BOOKED',
    EXCEPTION = 'EXCEPTION',
}

interface NormalizedSlot extends TimeType {
    status: SlotStatus;
    exceptionText?: string;
    id: string;
    service: ServiceType;
    color?: string;
    freeLabel: string;
}

interface CalendarWeekOverviewProps {
    booking?: BookingStateType;
    service?: ServiceType; // If provided, behaves like CalendarWeek (single service)
}

const servicesColors = [
    '#ECF7E5', '#C1D6F9', '#ECF7ED', '#DAF4D5', '#F8DEF8', '#F1E8E4',
    '#DFF9EA', '#D7D9ED', '#EAE2CC', '#C4F0F1', '#F2FCCB', '#FBD9D3',
    '#EFDEE6', '#EBFBDE', '#EBDCFA', '#F6D5CD', '#F0ECC8', '#F4E3DF',
];

// --- Logic ---

const getSlotStatus = (
    time: TimeType,
    service: ServiceType,
    overlappingExceptionText?: string
): { status: SlotStatus; exceptionText?: string } => {
    const freeSpots = getFreeSpots(time);

    // A slot with free spots is always bookable, even if it overlaps with an exception
    if (freeSpots > 0) {
        return { status: SlotStatus.BOOKABLE };
    }

    const publicException = getPublicExceptionReason(time);
    const hasException = hasTimeException(time) || !!overlappingExceptionText;
    const exceptionText = publicException || overlappingExceptionText;

    if (hasException && exceptionText) {
        return { status: SlotStatus.EXCEPTION, exceptionText };
    }

    const canQueue = canAddToQueue(time, service?.EnableBookingQueue);

    if (hasException) {
        return { status: SlotStatus.FULLY_BOOKED };
    }

    if (canQueue) {
        return { status: SlotStatus.QUEUEABLE };
    }

    return { status: SlotStatus.FULLY_BOOKED };
};

const normalizeDayTimes = (
    allTimesState: any,
    servicesData: ServiceType[],
    date: Date,
    config: Configuration,
    company: CompanyStateType,
    singleServiceMode: boolean
): NormalizedSlot[] => {
    const showBooked = !!company?.BookingSettings?.EnableShowBookedTimes;
    const serviceIdsToShow = config.serviceIdsToShow;
    const hideFullyBookedTimeslots =
        !singleServiceMode &&
        config.timesLayout === ConfigKeys.TIMES_LAYOUT_WEEKLY_OVERVIEW &&
        config.bookLayout === ConfigKeys.BOOK_LAYOUT_TIME_BASED &&
        config.listingLayout === ConfigKeys.LISTING_LAYOUT_ROWS &&
        !showBooked;

    let allSlots: NormalizedSlot[] = [];

    Object.keys(allTimesState).forEach((serviceId, index) => {
        const service = servicesData.find(s => Number(s.Id) === Number(serviceId));
        if (!service) return;

        // Filter out services if serviceIdsToShow is specified (in overview mode)
        if (!singleServiceMode && serviceIdsToShow) {
            if (Array.isArray(serviceIdsToShow) && !serviceIdsToShow.some(s => s.toString() === serviceId.toString())) {
                return;
            } else if (typeof serviceIdsToShow === 'string' || typeof serviceIdsToShow === 'number') {
                if (serviceIdsToShow.toString() !== serviceId.toString()) return;
            }
        }

        const timesState = allTimesState[serviceId];
        const rawTimes = timesState.times;

        const dayTimes = rawTimes
            .filter((t: TimeType) => isBefore(t.From, t.To))
            .filter((t: TimeType) => isSameDay(toDate(t.From), date));

        const filtered = showBooked ? dayTimes : dayTimes.filter((t: TimeType) => getFreeSpots(t) > 0);

        filtered.forEach((curr: TimeType) => {
            const overlapping = filtered.find((t: TimeType) =>
                getPublicExceptionReason(t) &&
                areIntervalsOverlapping(
                    { start: toDate(t.From), end: toDate(t.To) },
                    { start: toDate(curr.From), end: toDate(curr.To) }
                )
            );

            const { status, exceptionText } = getSlotStatus(
                curr,
                service,
                overlapping ? getPublicExceptionReason(overlapping) || undefined : undefined
            );

            if (hideFullyBookedTimeslots && status === SlotStatus.FULLY_BOOKED) {
                return;
            }

            allSlots.push({
                ...curr,
                status,
                exceptionText,
                service,
                id: `timeslot-${service.Id}-${format(curr.From, 'yyyy-MM-dd-HH-mm')}`,
                color: singleServiceMode ? undefined : servicesColors[index % servicesColors.length],
                freeLabel: curr.Free === 1 ? timesState.single : timesState.multiple,
            });
        });
    });

    return allSlots.sort((a, b) => {
        if (isBefore(toDate(a.From), toDate(b.From))) return -1;
        if (isBefore(toDate(b.From), toDate(a.From))) return 1;
        return 0;
    });
};

// --- Sub-components ---

const HeaderCell: React.FC<{ date: Date }> = ({ date }) => (
    <div {...c('dayHeader', { active: isSameDay(new Date(), date) })}>
        <div {...c('day')}>{format(date, 'd')}</div>
        <div {...c('month')}>{format(date, 'MMM')}</div>
        <div {...c('dayOfWeek')}>{format(date, 'E')}</div>
    </div>
);

const TimeSlot: React.FC<{
    slot: NormalizedSlot;
    showEndTime: boolean;
    bookedText?: string | null;
    showFreeCount: boolean;
    singleServiceMode: boolean;
    onSelect: (service: ServiceType, time: TimeType) => void;
}> = ({ slot, showEndTime, bookedText, showFreeCount, singleServiceMode, onSelect }) => {
    const isClickable = slot.status === SlotStatus.BOOKABLE || slot.status === SlotStatus.QUEUEABLE;

    if (singleServiceMode) {
        // When a slot is fully booked and a booked text is configured, show only
        // that text — hide the time (and its strikethrough) entirely.
        const hideTime = slot.status === SlotStatus.FULLY_BOOKED && !!bookedText;

        return (
            <button
                data-testid={`button-${slot.id}`}
                id={slot.id}
                {...c('timeSlot', {
                    addToQueue: slot.status === SlotStatus.QUEUEABLE,
                    fullyBooked: slot.status === SlotStatus.FULLY_BOOKED || slot.status === SlotStatus.EXCEPTION
                })}
                onClick={() => isClickable && onSelect(slot.service, slot)}
            >
                {!hideTime && (
                    <div {...c('time')}>
                        {formatFromTo(slot.From, slot.To, { renderEndTime: showEndTime })}
                    </div>
                )}

                {showFreeCount && slot.Free > 0 && (
                    <div {...c('freeTimesLeft')}>
                        {slot.Free} {slot.freeLabel}
                    </div>
                )}

                {slot.status === SlotStatus.EXCEPTION && (
                    <div>{slot.exceptionText}</div>
                )}

                {(slot.status === SlotStatus.FULLY_BOOKED || slot.status === SlotStatus.QUEUEABLE) && (
                    <div {...c('contentFullyBooked')}>
                        {bookedText
                            ? <div>{bookedText}</div>
                            : !bookedText && hasGroupBooking(slot.service)
                                ?
                                <Trans id="fullyBooked" />
                                : null}
                        {slot.status === SlotStatus.QUEUEABLE && (
                            <div><Trans id="addToQueue" /></div>
                        )}
                    </div>
                )}
            </button>
        );
    }

    // Overview Mode Presentation
    const { status, service } = slot;
    const isException = status === SlotStatus.EXCEPTION;
    const isFullyBooked = status === SlotStatus.FULLY_BOOKED;
    const isQueueable = status === SlotStatus.QUEUEABLE;
    const isGroup = hasGroupBooking(service);

    const showTooltip = (isFullyBooked || isException) && !!slot.exceptionText;
    const showFullyBookedLabel = !bookedText && (isException || ((isFullyBooked || isQueueable) && isGroup));

    return (
        <React.Fragment key={`${slot.From}-${slot.To}-${service.Id}`}>
            <button
                style={{ backgroundColor: slot.color }}
                data-testid={`button-${slot.id}`}
                id={slot.id}
                {...c('timeSlot timeslot', {
                    fullyBooked: isFullyBooked || isException,
                    addToQueue: isQueueable
                })}
                onClick={() => isClickable && onSelect(service, slot)}
            >
                <div {...c('timeAndName')}>
                    {(!isFullyBooked || !bookedText) && (
                        <>
                            <span {...c('time')}>
                                {format(slot.From, 'HH:mm')}-{format(slot.To, 'HH:mm')}
                            </span>
                            {showTooltip && (
                                <>
                                    {' '}
                                    <MdInfoOutline />
                                </>
                            )}
                        </>
                    )}
                    <>
                        <div {...c('serviceName')}>
                            {/* @ts-ignore */}
                            {service.Name}
                        </div>
                    </>
                </div>
                {slot.Free > 0 && showFreeCount && (
                    <div {...c('freeTimesLeft')}>
                        {slot.Free} {slot.freeLabel}
                    </div>
                )}
                {(isFullyBooked || isException || isQueueable) && bookedText}
                {showFullyBookedLabel && (
                    <div {...c('fullyBooked')}>
                        <Trans id="fullyBooked"></Trans>
                    </div>
                )}
                {isQueueable ? (
                    <div {...c("freeTimesLeft")}>
                        <Trans id="addToQueue" />
                    </div>
                ) : null}
            </button>
            {showTooltip && (
                <UncontrolledTooltip target={slot.id} container={tooltipContainer ? tooltipContainer : undefined}>
                    {slot.exceptionText}
                </UncontrolledTooltip>
            )}
        </React.Fragment>
    );
};

const DayColumn: React.FC<{
    date: Date;
    slots: NormalizedSlot[];
    isLoading: boolean;
    showEndTime: boolean;
    bookedText?: string | null;
    showFreeCount: boolean;
    singleServiceMode: boolean;
    onSelect: (service: ServiceType, time: TimeType) => void;
}> = ({ date, slots, isLoading, showEndTime, bookedText, showFreeCount, singleServiceMode, onSelect }) => (
    <div {...c('column', { noTimes: slots.length === 0, loading: isLoading })} style={{ width: `${100 / 7}%` }}>
        {slots.length > 0 ? (
            slots.map((slot, index) => (
                <TimeSlot
                    key={`${slot.id}-${index}`}
                    slot={slot}
                    showEndTime={showEndTime}
                    bookedText={bookedText}
                    showFreeCount={showFreeCount}
                    singleServiceMode={singleServiceMode}
                    onSelect={onSelect}
                />
            ))
        ) : (
            <Trans id="noTimes" />
        )}
    </div>
);

// --- Main Component ---

export const CalendarWeekOverview: React.FC<CalendarWeekOverviewProps> = ({ service }) => {
    const dispatch = useDispatch();
    const config = useSelector((state: ApplicationState) => state.configuration.data);
    const company = useSelector((state: ApplicationState) => state.company.data);
    const timesState = useSelector((state: ApplicationState) => state.times);
    const services = useSelector((state: ApplicationState) => state.services);

    const singleServiceMode = !!service;

    const onSelect = useCallback((selectedService: ServiceType, time: TimeType) => {
        dispatch({ type: 'SELECT_TIME', service: selectedService, time });
    }, [dispatch]);

    const days = useMemo(() => {
        const start = startOfWeek(toDate(config.navigationDate));
        return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }, [config.navigationDate]);

    const timesToProcess = useMemo(() => {
        if (singleServiceMode) {
            return service ? { [service.Id]: timesState[service.Id] } : {};
        }

        // Only consider the services that are actually shown. Hidden services are
        // still INIT'd as loading by fetchServices but never get their times fetched
        // (fetchServicesTimes is scoped to serviceIdsToShow), so including them here
        // would keep `isLoading` stuck true and suppress the next-available fallback.
        const serviceIdsToShow = config.serviceIdsToShow;
        if (!serviceIdsToShow) return timesState;

        const isShown = (serviceId: string) =>
            Array.isArray(serviceIdsToShow)
                ? serviceIdsToShow.some(s => s.toString() === serviceId)
                : serviceIdsToShow.toString() === serviceId;

        return Object.keys(timesState).reduce((acc, serviceId) => {
            if (isShown(serviceId)) acc[serviceId] = timesState[serviceId];
            return acc;
        }, {} as typeof timesState);
    }, [singleServiceMode, service, timesState, config.serviceIdsToShow]);

    const columnsData = useMemo(() => {
        if (!services.data.length || Object.keys(timesToProcess).length === 0) return [];

        return days.map(date => ({
            date,
            slots: normalizeDayTimes(timesToProcess, services.data, date, config, company, singleServiceMode)
        }));
    }, [timesToProcess, services.data, days, config, company, singleServiceMode]);

    const isLoading = Object.keys(timesToProcess).some(key => timesToProcess[key]?.isLoading);
    const noTimes = columnsData.every(col => col.slots.length === 0);

    let nextAvailableTime: TimeType | undefined;
    if (noTimes && !isLoading) {
        Object.keys(timesToProcess).forEach(key => {
            const candidate = timesToProcess[key]?.nextAvailableTime;
            if (
                candidate?.From &&
                (!nextAvailableTime || isBefore(toDate(candidate.From), toDate(nextAvailableTime.From)))
            ) {
                nextAvailableTime = candidate;
            }
        });
    }

    const bookedText = company?.WidgetSettings?.BookedTimeSlotText || config.bookedTimeSlotText;

    const showFreeCount = singleServiceMode
        ? ((service?.GroupBooking.Active || service?.MultipleResource.Active) && !!company?.BookingSettings?.ShowFreeTimesLeft)
        : !!company?.BookingSettings?.ShowFreeTimesLeft;

    return (
        <div {...c(singleServiceMode ? '' : 'weekOverview')}>
            <div {...c('head')}>
                {days.map((date, i) => <HeaderCell key={i} date={date} />)}
            </div>
            <div {...c('body')}>
                {columnsData.map(({ date, slots }, i) => (
                    <DayColumn
                        key={i}
                        date={date}
                        slots={slots}
                        isLoading={isLoading}
                        showEndTime={config.showEndTimeOnTimeslots}
                        bookedText={bookedText}
                        showFreeCount={showFreeCount}
                        singleServiceMode={singleServiceMode}
                        onSelect={onSelect}
                    />
                ))}
            </div>
            {noTimes && !isLoading && (
                config.showNextAvailableTime && nextAvailableTime?.From ? (
                    <Panel title={<Trans id="nextAvailableTime" />}>
                        <Media list style={{ paddingLeft: 0 }}>
                            <Media>
                                <Media body>
                                    <div>
                                        {formatFromTo(nextAvailableTime.From, nextAvailableTime.To, {
                                            renderDate: true,
                                        })}
                                    </div>
                                </Media>
                                <Media right align="center">
                                    <Button
                                        primary
                                        onClick={() => {
                                            dispatch({ type: 'CHANGE_NAVIGATION_DATE', date: nextAvailableTime!.From });
                                            dispatch({ type: 'FETCH_SERVICES_TIMES' });
                                        }}
                                    >
                                        <Trans id="showDay" />
                                    </Button>
                                </Media>
                            </Media>
                        </Media>
                    </Panel>
                ) : (
                    <Panel>
                        <Trans id="noTimes" />
                    </Panel>
                )
            )}
        </div>
    );
};

export default CalendarWeekOverview;
